/**
 * `string-bytes` (`LN-WGT02`, the shape proposed in the `LN-STL-M3` draft): a
 * `std::string` built one code point at a time, each code point becoming its
 * UTF-8 bytes, so `size()` is seen counting bytes, not letters.
 *
 * <div class="pc-widget" data-widget="string-bytes" data-text="মা"
 *      data-steps="[{&quot;glyph&quot;:&quot;ম&quot;,&quot;codepoint&quot;:&quot;U+09AE&quot;,&quot;bytes&quot;:[224,166,174],&quot;size&quot;:3},
 *                   {&quot;glyph&quot;:&quot;া&quot;,&quot;codepoint&quot;:&quot;U+09BE&quot;,&quot;bytes&quot;:[224,166,190],&quot;size&quot;:6}]"
 *      data-caption="Each Bangla code point is three bytes.">
 *   <p class="pc-widget-fallback">ম is 3 bytes and া is 3 more: size() is 6.</p>
 * </div>
 *
 * `data-text` (optional): the whole string, shown above the bytes.
 * `data-steps` (required): one step per code point, in order, each
 * `{ glyph, codepoint, bytes, size }`: `glyph` the code point as text,
 * `codepoint` its `U+` name, `bytes` its UTF-8 bytes as numbers 0 to 255 (one
 * to four), `size` what `size()` returns after it, which must be the running
 * total of the bytes. `data-caption` (optional). The first frame is step 1;
 * data that does not add up leaves the fallback. A reviewer may add optional
 * fields only.
 */

import {
  attachStepper,
  buildShell,
  el,
  isIndex,
  isNonEmptyString,
  isRecord,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

interface ByteStep {
  glyph: string;
  codepoint: string;
  bytes: number[];
  size: number;
}

const isSteps = (value: unknown): value is ByteStep[] => {
  if (!Array.isArray(value) || value.length === 0) return false;
  let total = 0;
  return value.every((step) => {
    if (
      !isRecord(step) ||
      !isNonEmptyString(step.glyph) ||
      typeof step.codepoint !== "string" ||
      !/^U\+[0-9A-Fa-f]{4,6}$/.test(step.codepoint) ||
      !Array.isArray(step.bytes) ||
      step.bytes.length < 1 ||
      step.bytes.length > 4 ||
      !step.bytes.every((byte) => isIndex(byte) && byte <= 255) ||
      !isIndex(step.size)
    ) {
      return false;
    }
    total += step.bytes.length;
    return step.size === total;
  });
};

/**
 * A combining mark (a vowel sign, the nukta) drawn alone sits on nothing, so
 * it is drawn on a dotted circle, the way Unicode's charts show it (the
 * `LN-STL-M3` review). The pattern is built at run time because a `\p{M}`
 * literal needs a newer regex target than the build's ES2017.
 */
const COMBINING = new RegExp("^\\p{M}+$", "u");
export const visibleGlyph = (glyph: string) => (COMBINING.test(glyph) ? `◌${glyph}` : glyph);

const hex = (byte: number) => byte.toString(16).toUpperCase().padStart(2, "0");

export function renderStringBytes(host: HTMLElement, text: WidgetText): boolean {
  const steps = parseJson(host.dataset.steps, isSteps);
  if (!steps) return false;

  const shell = buildShell(host, {
    label: text("labelBytes"),
    caption: host.dataset.caption,
  });

  if (host.dataset.text) shell.body.append(el("p", "pc-sb-whole", host.dataset.text));
  const built = el("p", "pc-sb-text");
  const groups = el("div", "pc-sb-groups");
  const note = liveNote();
  shell.body.append(built, groups, note);

  const drawn = steps.map((step) => {
    const group = el("div", "pc-sb-group");
    group.append(el("span", "pc-sb-glyph", visibleGlyph(step.glyph)));
    group.append(el("span", "pc-vec-block-label", step.codepoint.toUpperCase()));
    const row = el("div", "pc-widget-cells pc-widget-cells--tight");
    for (const byte of step.bytes) {
      const cell = el("div", "pc-cell");
      cell.append(el("span", "pc-cell-value", String(byte)));
      cell.append(el("span", "pc-cell-meta", `0x${hex(byte)}`));
      row.append(cell);
    }
    group.append(row);
    groups.append(group);
    return group;
  });

  attachStepper(
    shell,
    steps.length,
    (index) => {
      const step = steps[index];
      drawn.forEach((group, i) => {
        group.hidden = i > index;
        group.classList.toggle("is-active", i === index);
      });
      built.textContent = "";
      built.append(
        el("code", undefined, `"${steps.slice(0, index + 1).map((s) => s.glyph).join("")}"`),
        el("span", "pc-vec-stats", `size() ${step.size}`),
      );
      note.textContent = text("bytesStep", {
        glyph: step.glyph,
        codepoint: step.codepoint,
        count: step.bytes.length,
        size: step.size,
      });
    },
    text,
    { liveReadout: false },
  );
  return true;
}
